import { useState } from 'react';
import { useAdminData } from '../context/AdminDataContext';
import { Plus, X, Tag } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
  categories: { label: 'Project Categories', color: 'bg-[#47ace2]/10 text-[#47ace2] border-[#47ace2]/25' },
  technologies: { label: 'Technologies / Tags', color: 'bg-purple-50 text-purple-600 border-purple-200' },
  statuses: { label: 'Project Statuses', color: 'bg-green-50 text-green-600 border-green-200' },
};

export default function FiltersPage() {
  const { filterTags, addFilterTag, removeFilterTag } = useAdminData();
  const [newTags, setNewTags] = useState({ categories: '', technologies: '', statuses: '' });

  const handleAdd = async (cat) => {
    const tag = newTags[cat].trim();
    if (!tag) return;
    if (filterTags[cat]?.includes(tag)) { toast.error(`"${tag}" already exists`); return; }
    try {
      await addFilterTag(cat, tag);
      setNewTags(prev => ({ ...prev, [cat]: '' }));
      toast.success(`Tag "${tag}" added`);
    } catch (err) {
      toast.error(err.message || 'Failed to add tag');
    }
  };

  const handleRemove = async (cat, tag) => {
    try {
      await removeFilterTag(cat, tag);
      toast.success(`Tag "${tag}" removed`);
    } catch (err) {
      toast.error(err.message || 'Failed to remove tag');
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-outfit">Filter Tags Management</h1>
        <p className="text-slate-400 font-manrope text-sm mt-1">Manage dynamic filter categories and tags for projects</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {Object.entries(CATEGORY_CONFIG).map(([cat, config]) => (
          <div key={cat} className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="w-8 h-8 rounded-xl bg-[#47ace2]/10 flex items-center justify-center">
                <Tag className="w-4 h-4 text-[#47ace2]" />
              </div>
              <h2 className="text-slate-700 font-semibold font-outfit text-sm">{config.label}</h2>
              <span className="ml-auto text-slate-400 text-xs font-manrope bg-gray-100 px-2 py-0.5 rounded-lg">{filterTags[cat]?.length || 0}</span>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTags[cat]}
                  onChange={e => setNewTags(prev => ({ ...prev, [cat]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleAdd(cat)}
                  placeholder="New tag..."
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2] transition-colors"
                />
                <button
                  onClick={() => handleAdd(cat)}
                  className="p-2 bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-xl transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 min-h-[80px]">
                {filterTags[cat]?.length === 0 && (
                  <p className="text-slate-400 text-sm font-manrope">No tags yet. Add one above.</p>
                )}
                {filterTags[cat]?.map(tag => (
                  <span key={tag} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-manrope font-medium border ${config.color}`}>
                    {tag}
                    <button onClick={() => handleRemove(cat, tag)} className="hover:opacity-60 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#f0f8ff] border border-[#47ace2]/20 rounded-2xl p-5">
        <p className="text-slate-500 text-sm font-manrope">
          💡 <strong className="text-slate-700">Tip:</strong> These filter tags power the project search and filter UI on the public website.
          Changes here reflect in the filter bar immediately.
        </p>
      </div>
    </div>
  );
}
